import { useState } from "react";
import { useDashboardContext } from "../../../contexts/dashboard-context";
import React from "react";
import { useAppContext } from "../../../hooks/useAppContext";
import { useArrowKeyZoneMove } from "../../../hooks/useArrowKeyZoneMove";
import { useCopyPasteZone } from "../../../hooks/useCopyPasteZone";
import { useIsTransformMode } from "../../../hooks/useIsTransformMode";
import { useKeyDown } from "../../../hooks/useKeyboard";
import { useMenus, MenuIds } from "../../../hooks/useMenus";
import { useSelectedZone, useLogicalSelectedPage } from "../../../hooks/useSelection";
import { ZoneDepthDropdown } from "../../zone-depth-dropdown/zone-depth-dropdown";
import { TopMenu } from "../top-menu";
import { Tooltip } from "../../tooltip/tooltip";
import { useLabels } from "../../../hooks/useLabels";
import { labels } from "./labels";
import { useTopPhotoMenu } from "../../../hooks/use-photo-menu";
import { useIsSwapMode } from "../../slideshow-preview/hooks/use-photo-swapping";
import { useAutoStopSwapMode } from "../../slideshow-preview/hooks/use-auto-stop-swapmode";

export function PhotoTopMenu() {
	const selectedZone = useSelectedZone();
	const selectedPage = useLogicalSelectedPage();
	const dashboard = useDashboardContext();
	const { googleAnalytics } = useAppContext();
	const [isTransformMode, setIsTransformMode] = useIsTransformMode();

	const photoZone = eXpress.core.isPhotoZone(selectedZone) ? selectedZone : undefined;

	const {
		hide,
		showTransform,
		canTransform,
		showSwap,
		canSwap,
		showRotatePhoto,
		canRotatePhoto,
		showEffects,
		canApplyEffects,
		showPhotoEditing,
		canApplyPhotoEditing,
		showDeletePhotoZone,
		canDeletePhotoZone,
		showClearPhotoZone,
		canClearPhotoZone,
	} = useTopPhotoMenu();

	const {
		menuId,
		actions: { setMenu },
	} = useMenus();

	const { isSwapModeActive, toggleSwapMode } = useIsSwapMode();

	// add an event listener to disable swap mode when clicking outside of swap mode button or a valid drop target
	useAutoStopSwapMode();

	// Register canvas shortcuts
	useCopyPasteZone({ zone: photoZone, destinationPage: selectedPage ?? undefined });
	useArrowKeyZoneMove({ zone: photoZone });

	const [openDepthDropDown, setOpenDepthDropDown] = useState(menuId === MenuIds.PhotoZoneDepth);
	const [openOverflowDepthDropDown, setOpenOverflowDepthDropDown] = useState(
		menuId === MenuIds.PhotoZoneDepth,
	);
	const [overflowOpen, setOverflowOpen] = useState(false);

	const [
		[
			deleteText,
			rotateText,
			clearText,
			editText,
			filterText,
			transformText,
			positionText,
			photoSwapText,
		],
	] = useLabels([
		labels.delete,
		labels.rotate,
		labels.clear,
		labels.photoEdit,
		labels.filters,
		labels.transform,
		labels.photoPosition,
		labels.photoSwap,
	]);

	// Handle standard keyboard deletion logic
	useKeyDown((event) => {
		if (event.key === "Delete" && photoZone) {
			if (showDeletePhotoZone && canDeletePhotoZone) {
				dashboard.removeZone(photoZone);
				setMenu(MenuIds.Root);
			} else if (showClearPhotoZone && canClearPhotoZone) {
				dashboard.removePhotoFromZones([photoZone]);
			}
		}
	});

	if (hide) return null;

	// Action wrappers matches bottom menu actions
	const handleRotate = () => {
		const photoLook = photoZone?.getPhotoLook();
		if (dashboard.canRotatePhoto(photoLook)) {
			dashboard.rotatePhoto(photoLook);
		}
		googleAnalytics.trackEventToGa({ trackingAction: "RotateClicked" });
	};

	const handleClear = () => {
		const photoZones = photoZone ? [photoZone] : undefined;
		if (dashboard.canRemovePhotoFromZones(photoZones)) {
			dashboard.removePhotoFromZones(photoZones);
		}
		googleAnalytics.trackEventToGa({ trackingAction: "PhotoZoneClearClicked" });
	};

	const handleDelete = () => {
		if (photoZone && dashboard.canRemoveZone(photoZone)) {
			dashboard.removeZone(photoZone);
			setMenu(MenuIds.Root);
			googleAnalytics.trackEventToGa({ trackingAction: "PhotoZoneDeleteClicked" });
		}
	};

	if (isTransformMode) return null;

	return (
		<TopMenu.Root>
			<TopMenu.Bar>
				{/* Photo Transform */}
				{showTransform && (
					<TopMenu.Group hideLevel={3}>
						<Tooltip text={transformText}>
							<TopMenu.Icon
								iconName="photo-transform"
								disabled={!canTransform}
								checked={isTransformMode}
								onClick={() => {
									setIsTransformMode(true);
									googleAnalytics.trackEventToGa({
										trackingAction: "PhotoZoneTransformClicked",
									});
								}}
							/>
						</Tooltip>
					</TopMenu.Group>
				)}
				{showSwap && (
					<TopMenu.Group hideLevel={3} className="photo-swap">
						<Tooltip text={photoSwapText}>
							<TopMenu.Icon
								iconName="photo-swap"
								disabled={!canSwap}
								checked={isSwapModeActive}
								onClick={(e) => {
									e.stopPropagation(); // Prevent triggering document click listener that disables swap mode
									toggleSwapMode();
								}}
							/>
						</Tooltip>
					</TopMenu.Group>
				)}

				{/* Filters / Image Sliders Option */}
				{showEffects && !showPhotoEditing ? (
					<TopMenu.Group hideLevel={4}>
						<Tooltip text={filterText}>
							<TopMenu.Icon
								iconName="photo-filter"
								disabled={!canApplyEffects}
								checked={menuId === MenuIds.PhotoFilters}
								onClick={() => {
									setMenu(
										menuId === MenuIds.PhotoFilters
											? MenuIds.Photo
											: MenuIds.PhotoFilters,
									);
									googleAnalytics.trackEventToGa({
										trackingAction: "FiltersClicked",
									});
								}}
							/>
						</Tooltip>
					</TopMenu.Group>
				) : (
					(showEffects || showPhotoEditing) && (
						<TopMenu.Group hideLevel={4}>
							<Tooltip text={editText}>
								<TopMenu.Icon
									iconName="photo-sliders"
									disabled={
										showEffects &&
										!canApplyEffects &&
										showPhotoEditing &&
										!canApplyPhotoEditing
									}
									checked={menuId === MenuIds.PhotoFilters}
									onClick={() => {
										setMenu(
											menuId === MenuIds.PhotoFilters
												? MenuIds.Photo
												: MenuIds.PhotoFilters,
										);
										googleAnalytics.trackEventToGa({
											trackingAction: "PhotoEditClicked",
										});
									}}
								/>
							</Tooltip>
						</TopMenu.Group>
					)
				)}

				{/* Rotate Button */}
				{showRotatePhoto && (
					<TopMenu.Group hideLevel={5}>
						<Tooltip text={rotateText}>
							<TopMenu.Icon
								iconName="rotate"
								disabled={!canRotatePhoto}
								onClick={handleRotate}
							/>
						</Tooltip>
					</TopMenu.Group>
				)}

				{/* Depth / Arrangement Layer Dropdown */}
				<TopMenu.Group
					hideLevel={2}
					onVisibilityChange={(isVisible) => {
						if (!isVisible) setOpenDepthDropDown(false);
					}}
				>
					<Tooltip text={positionText}>
						<ZoneDepthDropdown
							open={openDepthDropDown}
							onOpenChange={setOpenDepthDropDown}
						/>
					</Tooltip>
				</TopMenu.Group>

				{/* Trash/Clear Action Items */}
				<TopMenu.Group>
					{showDeletePhotoZone ? (
						<Tooltip text={deleteText}>
							<TopMenu.Icon
								iconName="bin"
								disabled={!canDeletePhotoZone}
								onClick={handleDelete}
							/>
						</Tooltip>
					) : showClearPhotoZone ? (
						<Tooltip text={clearText}>
							<TopMenu.Icon
								iconName="bin"
								disabled={!canClearPhotoZone}
								onClick={handleClear}
							/>
						</Tooltip>
					) : null}
				</TopMenu.Group>

				{/* Contextual Overflow Trigger */}
				<TopMenu.Group showLevel={1}>
					<TopMenu.Icon
						iconName="dots-horizontal"
						onClick={() => setOverflowOpen((o) => !o)}
					/>
				</TopMenu.Group>
			</TopMenu.Bar>

			{/* Overflow Dropdown Structure */}
			<TopMenu.Group showLevel={1} style={{ position: "relative" }}>
				<TopMenu.OverflowBar show={overflowOpen}>
					{showTransform && (
						<TopMenu.Group showLevel={3}>
							<Tooltip text={transformText}>
								<TopMenu.Icon
									iconName="photo-transform"
									disabled={!canTransform}
									checked={isTransformMode}
									onClick={() => setIsTransformMode(true)}
								/>
							</Tooltip>
						</TopMenu.Group>
					)}

					{showEffects && !showPhotoEditing ? (
						<TopMenu.Group showLevel={4}>
							<Tooltip text={filterText}>
								<TopMenu.Icon
									iconName="photo-filter"
									disabled={!canApplyEffects}
									checked={menuId === MenuIds.PhotoFilters}
									onClick={() => setMenu(MenuIds.PhotoFilters)}
								/>
							</Tooltip>
						</TopMenu.Group>
					) : (
						(showEffects || showPhotoEditing) && (
							<TopMenu.Group showLevel={4}>
								<Tooltip text={editText}>
									<TopMenu.Icon
										iconName="photo-sliders"
										disabled={
											showEffects &&
											!canApplyEffects &&
											showPhotoEditing &&
											!canApplyPhotoEditing
										}
										checked={menuId === MenuIds.PhotoFilters}
										onClick={() => setMenu(MenuIds.PhotoFilters)}
									/>
								</Tooltip>
							</TopMenu.Group>
						)
					)}

					{showRotatePhoto && (
						<TopMenu.Group showLevel={5}>
							<Tooltip text={rotateText}>
								<TopMenu.Icon
									iconName="rotate"
									disabled={!canRotatePhoto}
									onClick={handleRotate}
								/>
							</Tooltip>
						</TopMenu.Group>
					)}

					<TopMenu.Group
						showLevel={2}
						onVisibilityChange={(isVisible) => {
							if (!isVisible) setOpenOverflowDepthDropDown(false);
						}}
					>
						<ZoneDepthDropdown
							open={openOverflowDepthDropDown}
							onOpenChange={setOpenOverflowDepthDropDown}
						/>
					</TopMenu.Group>
				</TopMenu.OverflowBar>
			</TopMenu.Group>
		</TopMenu.Root>
	);
}
